import React from "react";
import {
  FiRefreshCw,
  FiRotateCw,
  FiSearch,
  FiSliders,
} from "react-icons/fi";
import { HiOfficeBuilding, HiUserGroup } from "react-icons/hi";

import Reveal from "../../../components/ui/Reveal";

import { TROMBINO_TABS } from "../data/trombinosUiData";
import { cn } from "../utils/trombinosUtils";

export default function TrombinoFilters({
  loading,
  filters,
  services = [],
  stats,
  total = 0,
  allTotal = 0,
  onChange,
  onReset,
  onRefresh,
}) {
  const getCount = (tab) => {
    if (tab === "elected") return stats.elected;
    if (tab === "personnel") return stats.personnel;
    return stats.total;
  };

  return (
    <Reveal>
      <section className="rounded-[2rem] border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-950 sm:p-6">
        <div className="grid gap-5 lg:grid-cols-[1fr_auto] lg:items-end">
          <div className="space-y-2">
            <label
              htmlFor="trombino-search"
              className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400"
            >
              <FiSearch className="h-4 w-4" />
              Recherche instantanée
            </label>

            <div className="relative">
              <FiSearch className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />

              <input
                id="trombino-search"
                type="text"
                value={filters.q}
                onChange={(event) => onChange("q", event.target.value)}
                placeholder="Nom, fonction, service, groupe…"
                className="h-12 w-full rounded-2xl border border-slate-200 bg-white pl-11 pr-4 text-sm font-semibold text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-[#1277B9] focus:ring-4 focus:ring-[#1277B9]/10 dark:border-slate-700 dark:bg-slate-950 dark:text-white dark:placeholder:text-slate-500"
              />
            </div>
          </div>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <button
              type="button"
              onClick={onReset}
              className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-5 text-sm font-bold text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:hover:bg-slate-900"
            >
              <FiRefreshCw className="h-4 w-4" />
              Réinitialiser
            </button>

            <button
              type="button"
              onClick={onRefresh}
              disabled={loading}
              className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 text-sm font-bold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:hover:bg-slate-900"
            >
              <FiRotateCw className={cn("h-4 w-4", loading && "animate-spin")} />
              Actualiser
            </button>
          </div>
        </div>

        <div className="mt-5 space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
              <HiUserGroup className="h-4 w-4" />
              Présentation
            </p>

            <p className="text-xs font-semibold text-slate-500 dark:text-slate-400">
              {loading
                ? "Chargement…"
                : `${total} affiché${total > 1 ? "s" : ""} sur ${allTotal}`}
            </p>
          </div>

          <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1 md:flex-wrap md:overflow-visible">
            {TROMBINO_TABS.map((tab) => {
              const active = filters.tab === tab.value;

              return (
                <button
                  key={tab.value}
                  type="button"
                  onClick={() => onChange("tab", tab.value)}
                  className={cn(
                    "inline-flex shrink-0 items-center gap-2 rounded-full border px-4 py-2 text-sm font-bold transition",
                    active
                      ? "border-[#1277B9] bg-[#1277B9] text-white shadow-sm"
                      : "border-slate-200 bg-white text-slate-700 hover:border-[#1277B9]/50 hover:bg-sky-50 hover:text-[#1277B9] dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:hover:bg-slate-900"
                  )}
                >
                  {tab.label}
                  <span
                    className={cn(
                      "rounded-full px-2 py-0.5 text-xs",
                      active
                        ? "bg-white/20 text-white"
                        : "bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-300"
                    )}
                  >
                    {getCount(tab.value)}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="mt-5 grid gap-3 md:grid-cols-[1fr_auto] md:items-end">
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
              <HiOfficeBuilding className="h-4 w-4" />
              Service municipal
            </label>

            <select
              value={filters.serviceId}
              onChange={(event) => onChange("serviceId", event.target.value)}
              className="h-12 w-full rounded-2xl border border-slate-200 bg-white px-4 text-sm font-bold text-slate-700 outline-none transition focus:border-[#1277B9] focus:ring-4 focus:ring-[#1277B9]/10 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
            >
              <option value="all">Tous les services</option>
              {services.map((service) => (
                <option key={service.id} value={service.id}>
                  {service.label}
                </option>
              ))}
            </select>
          </div>

          <div className="rounded-3xl border border-slate-200 bg-slate-50 px-5 py-4 dark:border-slate-800 dark:bg-slate-900/70">
            <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
              <FiSliders className="h-4 w-4" />
              Répartition
            </p>

            <div className="mt-3 flex flex-wrap gap-2 text-sm font-bold">
              <span className="rounded-full bg-amber-100 px-3 py-1 text-amber-700 dark:bg-amber-500/10 dark:text-amber-300">
                {stats.elected} élu{stats.elected > 1 ? "s" : ""}
              </span>
              <span className="rounded-full bg-sky-100 px-3 py-1 text-sky-700 dark:bg-sky-500/10 dark:text-sky-300">
                {stats.personnel} agent{stats.personnel > 1 ? "s" : ""}
              </span>
            </div>
          </div>
        </div>
      </section>
    </Reveal>
  );
}